import { Box, Typography } from "@mui/material";
import { useState, useEffect, useRef, useContext } from "react";
import ReactPlayer from "react-player";
import ProgressBar from "../ProgressBar";
import { GlobalContext } from "../Context";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useTranslation } from "react-i18next";

const initialState = {
  vId: 0,
  playing: false,
};

const VideoPlayer = ({
  video,
  widthPercent,
  watchList,
  currSubCategory,
  objState,
  recoVidSel,
  setIsVideoLoad,
}) => {
  const { t } = useTranslation("LevelUp");
  const { mobileView, tabletView, firebaseState } = useContext(GlobalContext);

  const nthSelector = () => {
    const perRow = Math.floor(100 / widthPercent);
    const paddingAmt = (100 - perRow * widthPercent) / (perRow - 1);
    const style = {
      mr: paddingAmt + "%",
    };

    if (mobileView) {
      return { "&:not(:nth-child(2n))": style };
    }
    if (tabletView) {
      return { "&:not(:nth-child(3n))": style };
    }
    return { "&:not(:nth-child(6n))": style };
  };
  const videoBoxStyle = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: widthPercent + "%",
    mb: "1rem",
    ...nthSelector(),
  };
  const vTitle = {
    m: "0.2rem 0 1rem",
    textAlign: "center",
  };
  const checkIconStyle = {
    position: "absolute",
    top: "5%",
    right: "50%",
    mr: "-60px",
    color: "green",
    backgroundColor: "lightgreen",
    zIndex: 1,
  };
  const recoVidBox = {
    mt: "-0.5rem",
    mb: "-0.75rem",
    backgroundColor: objState.color,
    width: "175px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid",
    borderColor: objState.logoColor,
    transform: "translate(0px, -15px)",
  };
  const playerWrapper = {
    width: "175px",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };
  const playerStyle = {
    border: recoVidSel === video.id ? "2px solid" : "0px solid",
    borderColor: recoVidSel === video.id ? objState.logoColor : "transparent",
  };

  const [videoProgress, setProgressState] = useState(0);
  const [active, setActive] = useState(initialState);
  const [contained, setContained] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFinished, setIsFinished] = useState(false);
  const [isPlayerLoad, setIsPlayerLoad] = useState(0);

  const player = useRef();

  let watchedVid = watchList.watchList?.find((listVid) => {
    return listVid.url === video.url;
  });

  useEffect(() => {
    setActive(initialState);
  }, [currSubCategory]);

  const handleProgress = (t) => {
    setProgressState(t.playedSeconds);
  };

  const handlePlay = (video) => {
    setIsFinished(false);
    setActive({ vId: video.id, playing: true });
    let existingVid = watchList.watchList?.find((listVid) => {
      return listVid.url === video.url;
    });
    if (existingVid) {
      setContained(true);
    }
    if (!existingVid) {
      setContained(false);
    }
  };

  const percentComplete = (video) => {
    const progressBarWidth = (videoProgress / video.duration) * 100;
    return progressBarWidth > 95 ? 100 : progressBarWidth;
  };

  const handleExisting = (video) => {
    let existingVid = watchList.watchList?.find((listVid) => {
      return listVid.url === video.url;
    });
    existingVid.percentWatched = percentComplete(video);
  };

  const handleNew = (video) => {
    const newVideo = {
      duration: video.duration,
      id: video.id,
      url: video.url,
    };
    newVideo.percentWatched = percentComplete(video);
    newVideo.percentWatched && watchList.watchList?.push(newVideo);
  };

  const handleEnd = (video) => {
    setIsFinished((videoProgress / video.duration) * 100 > 95);
    //video counted completed at 95%
  };

  const handlePause = async (video) => {
    const activeVid = {
      vId: video.id,
      playing: false,
    };
    setActive(activeVid);
    contained ? handleExisting(video) : handleNew(video);
    watchList.watchList && (await firebaseState.updateWatchList(watchList));
  };

  const handleReady = (player, video) => {
    watchedVid = watchList.watchList?.find((listVid) => {
      return listVid.url === video.url;
    });
    if (watchedVid?.percentWatched > 95) {
      player.current.seekTo(0, "seconds");
      setIsFinished(true);
    } else {
      const timeWatched = (watchedVid?.percentWatched / 100) * video.duration;
      timeWatched
        ? player.current.seekTo(timeWatched, "seconds")
        : player.current.seekTo(0, "seconds");
      setIsFinished(false);
    }
    setIsPlayerLoad(isPlayerLoad + 1);
    setIsVideoLoad((prevState) => prevState + 1);
    setIsLoading(false);
  };

  return (
    <Box sx={videoBoxStyle} id={video.id}>
      {recoVidSel === video.id && (
        <Box sx={recoVidBox}>
          <Typography
            variant="h4"
            color={objState.logoColor}
            textTransform="uppercase">
            {t("recommended")}
          </Typography>
        </Box>
      )}
      {isFinished && <CheckBoxIcon sx={checkIconStyle} />}
      <Box sx={playerWrapper}>
        <ReactPlayer
          id={video.id}
          url={video.url}
          controls={true}
          width="175px"
          height="300px"
          style={playerStyle}
          onProgress={(t) => handleProgress(t, video)}
          onPlay={() => handlePlay(video)}
          onPause={() => handlePause(video)}
          onEnded={() => handleEnd(video)}
          loop={false}
          ref={player}
          onReady={() => handleReady(player, video)}
        />
        {!isLoading && (
          <ProgressBar
            barWidth={percentComplete(video)}
            isActive={active}
            id={video.id}
            prevWidth={watchedVid?.percentWatched || 0}
            isPlayerLoad={isPlayerLoad}
          />
        )}
        <Typography id={video.id} variant="body1" sx={vTitle}>
          {video.title}
        </Typography>
      </Box>
    </Box>
  );
};

export default VideoPlayer;
